// Estado offline para DevTools (pestana Offline, comando `offline`): conexion
// al backend, service worker, almacenamiento y cuanto hay guardado en
// IndexedDB y localStorage. Solo conteos y tamanos: nunca los valores.

import { isBackendKnownDown, isBackendReachable } from "@/lib/offline/connection-signal";
import { isOffline } from "@/lib/offline/is-offline";
import { isConvexBlocked } from "./convex-socket";
import { groupLocalStorage, type StorageGroup } from "./offline-groups";

async function countStores(name: string): Promise<Array<{ name: string; count: number }>> {
  return new Promise((resolve) => {
    const req = indexedDB.open(name);
    req.onerror = () => resolve([]);
    req.onblocked = () => resolve([]);
    req.onsuccess = () => {
      const db = req.result;
      const names = [...db.objectStoreNames];
      if (names.length === 0) {
        db.close();
        resolve([]);
        return;
      }
      const tx = db.transaction(names, "readonly");
      const out: Array<{ name: string; count: number }> = [];
      for (const store of names) {
        const c = tx.objectStore(store).count();
        c.onsuccess = () => out.push({ name: store, count: c.result });
      }
      tx.oncomplete = () => {
        db.close();
        resolve(out.sort((a, b) => b.count - a.count));
      };
      tx.onerror = () => {
        db.close();
        resolve(out);
      };
    };
  });
}

export async function offlineSnapshot() {
  const mb = (n?: number) => (n === undefined ? null : Math.round((n / 1_048_576) * 10) / 10);
  let estimate: StorageEstimate | null = null;
  try {
    estimate = (await navigator.storage?.estimate?.()) ?? null;
  } catch {
    estimate = null;
  }
  let databases: Array<{ name: string; version?: number; stores: Array<{ name: string; count: number }> }> = [];
  try {
    const list = (await indexedDB.databases?.()) ?? [];
    databases = await Promise.all(
      list
        .filter((d): d is IDBDatabaseInfo & { name: string } => Boolean(d.name))
        .map(async (d) => ({ name: d.name, version: d.version, stores: await countStores(d.name) })),
    );
  } catch {
    databases = [];
  }
  let local: StorageGroup[] = [];
  try {
    const entries: Array<[string, string]> = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) entries.push([key, localStorage.getItem(key) ?? ""]);
    }
    local = groupLocalStorage(entries);
  } catch {
    local = [];
  }
  return {
    backend: {
      reachable: isBackendReachable(),
      knownDown: isBackendKnownDown(),
      offlineMode: isOffline(),
      simulatedOffline: isConvexBlocked(),
    },
    navigatorOnLine: navigator.onLine,
    serviceWorker: navigator.serviceWorker?.controller?.state ?? null,
    storage: { usageMB: mb(estimate?.usage), quotaMB: mb(estimate?.quota) },
    indexedDB: databases.sort((a, b) => a.name.localeCompare(b.name)),
    localStorage: local,
    localfirst: localFirstSnapshot(),
  };
}

interface EngineLike {
  syncStatus(): { online: boolean; leader: boolean; pending: number; recovery: number; paused: boolean };
  recoveryItems(): ReadonlyArray<{ kind: string; error?: { code: string } }>;
  scopeState(scope: string): { status: string; cursor: number | null; failures: number; syncedAt?: number; readOnly?: boolean } | undefined;
  rows(scope: string): unknown[];
}

/**
 * Motor local-first (colecciones disponibles sin conexión): estado del sync y
 * de cada alcance activo. Solo conteos y estados, nunca valores.
 */
function localFirstSnapshot() {
  const engine = (window as unknown as { __heskalaLf?: EngineLike }).__heskalaLf;
  if (!engine) return null;
  let scopes: string[] = [];
  try {
    scopes = JSON.parse(localStorage.getItem("heskala:lf:enabled") ?? "[]");
  } catch {
    scopes = [];
  }
  return {
    sync: engine.syncStatus(),
    recovery: engine.recoveryItems().map((r) => ({ kind: r.kind, code: r.error?.code ?? null })),
    scopes: scopes.map((scope) => {
      const state = engine.scopeState(scope);
      return {
        scope,
        status: state?.status ?? "inactive",
        cursor: state?.cursor ?? null,
        failures: state?.failures ?? 0,
        readOnly: !!state?.readOnly,
        syncedAgoSec: state?.syncedAt ? Math.round((Date.now() - state.syncedAt) / 1000) : null,
        rows: engine.rows(scope).length,
      };
    }),
  };
}

export type OfflineSnapshot = Awaited<ReturnType<typeof offlineSnapshot>>;
